import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  Card,
  Typography,
  Button,
  Space,
  message,
  Tabs,
  Spin,
  Row,
  Col,
  Tag,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  CheckOutlined,
  FileTextOutlined,
  EyeOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import customerApi from '../services/customerApi';
import { Business } from '../services/businesses';
import { Task } from '../services/tasks';
import { Letter } from '../services/letters';
import { Dispatch } from '../services/dispatches';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

export const CustomerBusinessDetail: React.FC = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState<Business | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [letters, setLetters] = useState<Letter[]>([]);
  const [dispatches, setDispatches] = useState<Dispatch[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [loadingLetters, setLoadingLetters] = useState(false);
  const [loadingDispatches, setLoadingDispatches] = useState(false);
  const activeTab = searchParams.get('tab') || 'ssm';

  useEffect(() => {
    if (id) {
      loadBusiness(parseInt(id, 10));
    }
  }, [id]);

  useEffect(() => {
    if (business?.id) {
      // Load tasks, letters, dispatches for this business
      if (activeTab === 'tasks') {
        loadTasks();
      } else if (activeTab === 'letters') {
        loadLetters();
      } else if (activeTab === 'dispatch') {
        loadDispatches();
      }
      // SSM and Financial tabs will load reports automatically via their components
    }
  }, [business, activeTab]);

  const loadBusiness = async (businessId: number) => {
    setLoading(true);
    try {
      const response = await customerApi.get(`/customer/businesses/${businessId}`);
      setBusiness(response.data.data || response.data);
    } catch (error: any) {
      console.error('Failed to load business:', error);
      let errorMessage = 'Failed to load business';
      
      if (error.response) {
        errorMessage = error.response.data?.message || error.response.data?.error || `Server error: ${error.response.status}`;
        if (error.response.status === 401) {
          errorMessage = 'Unauthorized. Please login again.';
        } else if (error.response.status === 403) {
          errorMessage = 'Access denied.';
        } else if (error.response.status === 404) {
          errorMessage = 'Business not found.';
        }
      } else if (error.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      message.error(errorMessage);
      navigate('/customer/business');
    } finally {
      setLoading(false);
    }
  };

  const loadTasks = async () => {
    if (!business?.id) return;
    setLoadingTasks(true);
    try {
      const response = await customerApi.get('/customer/tasks', {
        params: { business_id: business.id }
      });
      setTasks(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const loadLetters = async () => {
    if (!business?.id) return;
    setLoadingLetters(true);
    try {
      const response = await customerApi.get('/customer/letters', {
        params: { business_id: business.id }
      });
      setLetters(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load letters:', error);
    } finally {
      setLoadingLetters(false);
    }
  };

  const loadDispatches = async () => {
    if (!business?.id) return;
    setLoadingDispatches(true);
    try {
      const response = await customerApi.get('/customer/dispatches', {
        params: { business_id: business.id }
      });
      setDispatches(response.data.data || []);
    } catch (error: any) {
      console.error('Failed to load dispatches:', error);
    } finally {
      setLoadingDispatches(false);
    }
  };

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key });
  };

  const formatTaskNumber = (id: number, referenceNumber?: string) => {
    return referenceNumber || `#${id.toString().padStart(7, '0')}`;
  };

  const formatLetterNumber = (id: number, referenceNumber?: string) => {
    return referenceNumber || `#${id.toString().padStart(7, '0')}`;
  };

  const formatDispatchNumber = (id: number, referenceNumber?: string) => {
    return referenceNumber || `#${id.toString().padStart(7, '0')}`;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const parsed = dayjs(dateString);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY') : dateString;
  };

  const getStatusInfo = (status?: string) => {
    switch (status) {
      case 'Plan':
        return { 
          color: '#1890ff', 
          progress: 0, 
          label: 'Plan',
          bgColor: '#e6f7ff',
          textColor: '#1890ff',
          tagBgColor: '#e6f7ff',
          tagBorderColor: '#91d5ff',
          tagTextColor: '#1890ff',
          dotColor: '#1890ff'
        };
      case 'Progress':
        return { 
          color: '#fa8c16', 
          progress: 40, 
          label: 'Progress',
          bgColor: '#fff7e6',
          textColor: '#fa8c16',
          tagBgColor: '#fff7e6',
          tagBorderColor: '#ffd591',
          tagTextColor: '#fa8c16',
          dotColor: '#fa8c16'
        };
      case 'Review':
        return { 
          color: '#ff4d4f', 
          progress: 70, 
          label: 'Review',
          bgColor: '#fff1f0',
          textColor: '#ff4d4f',
          tagBgColor: '#fff1f0',
          tagBorderColor: '#ffccc7',
          tagTextColor: '#ff4d4f',
          dotColor: '#ff4d4f'
        };
      case 'Approve':
        return { 
          color: '#13c2c2', 
          progress: 90, 
          label: 'Approve',
          bgColor: '#e6fffb',
          textColor: '#13c2c2',
          tagBgColor: '#e6fffb',
          tagBorderColor: '#87e8de',
          tagTextColor: '#13c2c2',
          dotColor: '#13c2c2'
        };
      case 'Completed':
        return { 
          color: '#52c41a', 
          progress: 100, 
          label: 'Completed',
          bgColor: '#f6ffed',
          textColor: '#52c41a',
          tagBgColor: '#f6ffed',
          tagBorderColor: '#b7eb8f',
          tagTextColor: '#52c41a',
          dotColor: '#52c41a'
        };
      default:
        return { 
          color: '#1890ff', 
          progress: 0, 
          label: 'Plan',
          bgColor: '#e6f7ff',
          textColor: '#1890ff',
          tagBgColor: '#e6f7ff',
          tagBorderColor: '#91d5ff',
          tagTextColor: '#1890ff',
          dotColor: '#1890ff'
        };
    }
  };

  const getLetterStatusInfo = (status?: string) => {
    switch (status) {
      case 'Read':
      return {
        bgColor: '#f6ffed',
        textColor: '#52c41a',
        tagBgColor: '#f6ffed',
        tagBorderColor: '#b7eb8f',
        tagTextColor: '#52c41a',
        dotColor: '#52c41a',
          label: 'Read'
      };
      case 'Unread':
        return {
          bgColor: '#fff7e6',
          textColor: '#ff8c00',
          tagBgColor: '#fff7e6',
          tagBorderColor: '#ffa940',
          tagTextColor: '#ff8c00',
          dotColor: '#ff8c00',
          label: 'Sent'
        };
      default:
      return {
        bgColor: '#e6f7ff',
        textColor: '#1890ff',
        tagBgColor: '#e6f7ff',
        tagBorderColor: '#91d5ff',
        tagTextColor: '#1890ff',
        dotColor: '#1890ff',
          label: 'Plan'
      };
    }
  };

  const getDispatchStatus = (dispatch: Dispatch) => {
    const mapStatus = (status: string | undefined): 'Pending' | 'Collect' | 'Return' => {
      if (!status) return 'Pending';
      const normalized = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
      if (['Pending', 'Collect', 'Return'].includes(normalized)) {
        return normalized as 'Pending' | 'Collect' | 'Return';
      }
      return 'Pending';
    };
    
    const mappedStatus = mapStatus(dispatch.status);
    
    const statusConfig = {
      Pending: {
        label: 'Pending',
        bgColor: '#E6F7FF',
        textColor: '#1890ff',
        tagBgColor: '#E6F7FF',
        tagBorderColor: '#91D5FF',
        tagTextColor: '#1890ff',
        dotColor: '#1890ff'
      },
      Collect: {
        label: 'Collect',
        bgColor: '#F6FFED',
        textColor: '#52C41A',
        tagBgColor: '#F6FFED',
        tagBorderColor: '#73D13D',
        tagTextColor: '#52C41A',
        dotColor: '#52C41A'
      },
      Return: {
        label: 'Return',
        bgColor: '#FFF7E6',
        textColor: '#FF8C00',
        tagBgColor: '#FFF7E6',
        tagBorderColor: '#FFA940',
        tagTextColor: '#FF8C00',
        dotColor: '#FF8C00'
      },
    };
    
    return statusConfig[mappedStatus];
  };

  if (loading || !business) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {/* Business Info Card */}
      <Card
        style={{
          marginBottom: 24,
          borderRadius: 8,
          border: '1px solid #d9d9d9',
          padding: 0,
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        bodyStyle={{ padding: 0 }}
        onClick={() => navigate(`/customer/businesses/${business.id}/edit`)}
      >
        {/* Header Section */}
        <div
          style={{
            backgroundColor: '#e6f7ff',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Title level={4} style={{ margin: 0, color: '#1890ff', fontWeight: 'bold' }}>
            {business.name}
          </Title>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/customer/business');
            }}
          >
            Back
          </Button>
        </div>
        
        {/* Divider Line */}
        <div
          style={{
            height: 1,
            backgroundColor: '#e6f7ff',
            width: '100%',
          }}
        />
        
        {/* Content Section */}
        <div
          style={{
            backgroundColor: '#fff',
            padding: '16px 20px',
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <Text style={{ color: '#666' }}>Registration No. : </Text>
            <Text style={{ color: '#1890ff' }}>{business.registration_no || '-'}</Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text style={{ color: '#666' }}>Entity : </Text>
            <Text style={{ color: '#1890ff' }}>{business.entity || '-'}</Text>
          </div>
          <div style={{ marginBottom: 8 }}>
            <Text style={{ color: '#666' }}>SST No. : </Text>
            <Text style={{ color: '#1890ff' }}>{business.sst_no || '-'}</Text>
          </div>
          <div>
            <Text style={{ color: '#666' }}>Tin No. : </Text>
            <Text style={{ color: '#1890ff' }}>{business.tin_no || '-'}</Text>
          </div>
        </div>
      </Card>

      {/* Tabs */}
      <Card style={{ background: '#fff' }}>
        <Tabs 
          activeKey={activeTab}
          onChange={handleTabChange}
        >
          <TabPane tab="SSM" key="ssm">
            <CustomerReportsList reportType="SSM" businessId={business?.id} />
          </TabPane>
          <TabPane tab="Financial" key="financial">
            <CustomerReportsList reportType="Financial" businessId={business?.id} />
          </TabPane>
          <TabPane tab="Tasks" key="tasks">
            {loadingTasks ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {tasks.map((task) => {
                  const statusInfo = getStatusInfo(task.status);
                  return (
                    <Col key={task.id} xs={24} sm={12} md={12} lg={12}>
                      <Card
                        style={{
                          height: '100%',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                          padding: 0,
                          overflow: 'hidden',
                          cursor: 'pointer',
                        }}
                        bodyStyle={{ padding: 0 }}
                        onClick={() => navigate(`/customer/tasks/${task.id}`)}
                      >
                        {/* Header Section */}
                        <div
                          style={{
                            backgroundColor: statusInfo.bgColor,
                            padding: '12px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                            {formatTaskNumber(task.id, task.reference_number)}
                          </Title>
                          <Tag
                            style={{
                              borderRadius: 16,
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: statusInfo.tagBgColor,
                              border: `1px solid ${statusInfo.tagBorderColor}`,
                              color: statusInfo.tagTextColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              margin: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: statusInfo.dotColor,
                                display: 'inline-block',
                              }}
                            />
                            {statusInfo.label}
                          </Tag>
                        </div>
                        
                        {/* Content Section */}
                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '16px',
                            position: 'relative',
                            minHeight: 200,
                          }}
                        >
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Type: </Text>
                            <Text style={{ color: '#000' }}>{task.taskType?.name || '-'}</Text>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Year: </Text>
                            <Text style={{ color: '#000' }}>{task.year || '-'}</Text>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Task: </Text>
                            <Text style={{ color: '#000' }}>{task.taskTemplate?.name || '-'}</Text>
                          </div>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Due: </Text>
                              <Text style={{ color: '#000' }}>{formatDate(task.due_date) || '-'}</Text>
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Deadline: </Text>
                              <Text style={{ color: '#000' }}>{formatDate(task.deadline) || '-'}</Text>
                            </Col>
                          </Row>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Quotation: </Text>
                              {task.quotation?.quotation_number ? (
                                <Button
                                  type="link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (task.quotation?.id) {
                                      navigate(`/quotations/${task.quotation.id}/edit`);
                                    }
                                  }}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {task.quotation.quotation_number}
                                </Button>
                              ) : (
                                <Text style={{ color: '#000' }}>-</Text>
                              )}
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Invoice: </Text>
                              {task.quotation?.invoice?.invoice_number ? (
                                <Button
                                  type="link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (task.quotation?.invoice?.id) {
                                      navigate(`/invoices/${task.quotation.invoice.id}/edit`);
                                    }
                                  }}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {task.quotation.invoice.invoice_number}
                                </Button>
                              ) : (
                                <Text style={{ color: '#000' }}>-</Text>
                              )}
                            </Col>
                          </Row>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Draft: </Text>
                              {task.draftReports && task.draftReports.length > 0 ? (
                                <Button
                                  type="link"
                                  icon={<FileTextOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/customer/tasks/${task.id}`);
                                  }}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  {task.draftReports[0].attachment?.split('/').pop() || 'report.update'}
                                </Button>
                              ) : (
                                <Text style={{ color: '#000' }}>-</Text>
                              )}
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Staff: </Text>
                              <Text style={{ color: '#000' }}>{task.staff?.name || '-'}</Text>
                            </Col>
                          </Row>
                          
                          {task.status === 'Review' && (
                            <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                              <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                style={{
                                  backgroundColor: '#52c41a',
                                  borderColor: '#52c41a',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  // Handle approve
                                }}
                              >
                                Approve
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            {!loadingTasks && tasks.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No tasks found
              </div>
            )}
          </TabPane>
          <TabPane tab="Letters" key="letters">
            {loadingLetters ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {letters.map((letter) => {
                  const statusInfo = getLetterStatusInfo(letter.status);
                  return (
                    <Col key={letter.id} xs={24} sm={12} md={12} lg={12}>
                      <Card
                        style={{
                          height: '100%',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                          padding: 0,
                          overflow: 'hidden',
                          cursor: 'pointer',
                        }}
                        bodyStyle={{ padding: 0 }}
                        onClick={() => navigate(`/customer/letters/${letter.id}`)}
                      >
                        {/* Header Section */}
                        <div
                          style={{
                            backgroundColor: statusInfo.bgColor,
                            padding: '12px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                            {formatLetterNumber(letter.id, letter.reference_number)}
                          </Title>
                          <Tag
                            style={{
                              borderRadius: 16,
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: statusInfo.tagBgColor,
                              border: `1px solid ${statusInfo.tagBorderColor}`,
                              color: statusInfo.tagTextColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              margin: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: statusInfo.dotColor,
                                display: 'inline-block',
                              }}
                            />
                            {statusInfo.label}
                          </Tag>
                        </div>
                        
                        {/* Content Section */}
                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '16px',
                            position: 'relative',
                            minHeight: 200,
                          }}
                        >
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Customer: </Text>
                            <Text style={{ color: '#000' }}>{letter.customer?.name || '-'}</Text>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Sender: </Text>
                            <Text style={{ color: '#000' }}>{letter.taskType?.name || letter.sender?.name || '-'}</Text>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Subject: </Text>
                            <Text style={{ color: '#000' }}>{letter.taskTemplate?.name || letter.subject?.name || '-'}</Text>
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Due: </Text>
                            <Text style={{ color: '#000' }}>{formatDate(letter.due_date) || '-'}</Text>
                          </div>
                          <Row gutter={16}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Letter: </Text>
                              {letter.items && letter.items.length > 0 ? (
                                <Button
                                  type="link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/customer/letters/${letter.id}`);
                                  }}
                                  style={{ padding: 0, height: 'auto' }}
                                >
                                  View
                                </Button>
                              ) : (
                                <Text style={{ color: '#000' }}>-</Text>
                              )}
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Staff: </Text>
                              <Text style={{ color: '#000' }}>{letter.taskType?.name || letter.sender?.name || '-'}</Text>
                            </Col>
                          </Row>
                          
                          {letter.status === 'sent' && (
                            <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                              <Button
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/customer/letters/${letter.id}`);
                                }}
                              >
                                Read
                              </Button>
                            </div>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            {!loadingLetters && letters.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No letters found
              </div>
            )}
          </TabPane>
          <TabPane tab="Dispatch" key="dispatch">
            {loadingDispatches ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Row gutter={[16, 16]}>
                {dispatches.map((dispatch) => {
                  const statusInfo = getDispatchStatus(dispatch);
                  return (
                    <Col key={dispatch.id} xs={24} sm={12} md={12} lg={12}>
                      <Card
                        style={{
                          height: '100%',
                          borderRadius: 8,
                          border: '1px solid #d9d9d9',
                          padding: 0,
                          overflow: 'hidden',
                          cursor: 'pointer',
                        }}
                        bodyStyle={{ padding: 0 }}
                        onClick={() => navigate(`/customer/dispatches/${dispatch.id}`)}
                      >
                        {/* Header Section */}
                        <div
                          style={{
                            backgroundColor: statusInfo.bgColor,
                            padding: '12px 16px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                          }}
                        >
                          <Title level={5} style={{ margin: 0, color: statusInfo.textColor, fontWeight: 'bold' }}>
                            {formatDispatchNumber(dispatch.id, dispatch.reference_number)}
                          </Title>
                          <Tag
                            style={{
                              borderRadius: 16,
                              padding: '4px 12px',
                              fontSize: '12px',
                              backgroundColor: statusInfo.tagBgColor,
                              border: `1px solid ${statusInfo.tagBorderColor}`,
                              color: statusInfo.tagTextColor,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              margin: 0,
                            }}
                          >
                            <span
                              style={{
                                width: 4,
                                height: 4,
                                borderRadius: '50%',
                                backgroundColor: statusInfo.dotColor,
                                display: 'inline-block',
                              }}
                            />
                            {statusInfo.label}
                          </Tag>
                        </div>
                        
                        {/* Content Section */}
                        <div
                          style={{
                            backgroundColor: '#fff',
                            padding: '16px',
                            position: 'relative',
                            minHeight: 200,
                          }}
                        >
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Customer: </Text>
                            <Text style={{ color: '#000' }}>{dispatch.customer?.name || '-'}</Text>
                          </div>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Type: </Text>
                              <Text style={{ color: '#000' }}>{dispatch.taskType?.name || '-'}</Text>
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Year: </Text>
                              <Text style={{ color: '#000' }}>{dispatch.year || '-'}</Text>
                            </Col>
                          </Row>
                          <div style={{ marginBottom: 12 }}>
                            <Text style={{ color: '#666' }}>Task: </Text>
                            <Text style={{ color: '#000' }}>{dispatch.task?.taskTemplate?.name || '-'}</Text>
                          </div>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Collect Due: </Text>
                              <Text style={{ color: '#000' }}>{formatDate(dispatch.collect_due) || '-'}</Text>
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Return Due: </Text>
                              <Text style={{ color: '#000' }}>{formatDate(dispatch.return_due) || '-'}</Text>
                            </Col>
                          </Row>
                          <Row gutter={16} style={{ marginBottom: 12 }}>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Status: </Text>
                              <Tag 
                                style={{
                                  backgroundColor: statusInfo.tagBgColor,
                                  borderColor: statusInfo.tagBorderColor,
                                  color: statusInfo.tagTextColor,
                                }}
                              >
                                {statusInfo.label}
                              </Tag>
                            </Col>
                            <Col span={12}>
                              <Text style={{ color: '#666' }}>Staff: </Text>
                              <Text style={{ color: '#000' }}>{dispatch.agent?.name || '-'}</Text>
                            </Col>
                          </Row>
                          
                          {['Collect', 'Return'].includes(dispatch.status || '') && (() => {
                            // Check if there's already an acknowledge record for the current status
                            const hasAcknowledgeRecord = dispatch.acknowledge_logs?.some((log: any) => {
                              if (dispatch.status === 'Collect') {
                                return log.to_status === 'Acknowledge (collect)';
                              } else if (dispatch.status === 'Return') {
                                return log.to_status === 'Acknowledge (return)';
                              }
                              return false;
                            });
                            
                            // Only show button if no acknowledge record exists for current status
                            if (!hasAcknowledgeRecord) {
                              return (
                          <div style={{ position: 'absolute', bottom: 16, right: 16 }}>
                            <Button
                                    type="primary"
                                    style={{
                                      backgroundColor: statusInfo.textColor,
                                      borderColor: statusInfo.tagBorderColor,
                                    }}
                              onClick={(e) => {
                                e.stopPropagation();
                                // Handle acknowledge
                                      navigate(`/customer/dispatches/${dispatch.id}`);
                              }}
                            >
                                    {dispatch.status === 'Collect' ? 'Collect Acknowledge' : 'Return Acknowledge'}
                            </Button>
                          </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
            {!loadingDispatches && dispatches.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No dispatches found
              </div>
            )}
          </TabPane>
          <TabPane tab="Files" key="files">
            {/* Files content - empty for now */}
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
              Files content coming soon
            </div>
          </TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

// Customer Reports List Component (for SSM and Financial)
interface CustomerReport {
  id: number;
  customer_id: number;
  business_id?: number | null;
  name: string;
  path: string;
  type: string;
  mime_type: string;
  size: number;
  folder: string | null;
  report_type: 'SSM' | 'Financial' | null;
  task: string | null;
  year: number | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

interface CustomerReportsListProps {
  reportType: 'SSM' | 'Financial';
  businessId?: number;
}

const CustomerReportsList: React.FC<CustomerReportsListProps> = ({ reportType, businessId }) => {
  const [reports, setReports] = useState<CustomerReport[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const response = await customerApi.get('/customer/reports');
        // Filter by report type AND business_id
        const filteredReports = (response.data.data || []).filter(
          (report: CustomerReport) => {
            const typeMatch = report.report_type === reportType;
            const businessMatch = businessId 
              ? (report.business_id === businessId)
              : (report.business_id === null || report.business_id === undefined);
            return typeMatch && businessMatch;
          }
        );
        setReports(filteredReports);
      } catch (error: any) {
        message.error(error.response?.data?.message || `Failed to load ${reportType} reports`);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [reportType, businessId]);

  const formatFileSize = (bytes: number): string => {
    if (bytes >= 1073741824) {
      return (bytes / 1073741824).toFixed(2) + ' GB';
    } else if (bytes >= 1048576) {
      return (bytes / 1048576).toFixed(2) + ' MB';
    } else if (bytes >= 1024) {
      return (bytes / 1024).toFixed(2) + ' KB';
    } else {
      return bytes + ' bytes';
    }
  };

  const getFileIcon = (type: string) => {
    const iconStyle = { fontSize: 48, color: '#ff4d4f' };
    return <FileTextOutlined style={iconStyle} />;
  };

  const handleView = async (report: CustomerReport) => {
    try {
      const response = await customerApi.get(`/customer/reports/${report.id}/view`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to view file');
    }
  };

  const handleDownload = async (report: CustomerReport) => {
    try {
      const response = await customerApi.get(`/customer/reports/${report.id}/download`, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { type: response.headers['content-type'] || report.mime_type });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = report.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error: any) {
      message.error('Failed to download file');
    }
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', textAlign: 'center', padding: '40px' }} />;
  }

  return (
    <div style={{ padding: '24px', background: '#fff' }}>
      <Row gutter={[16, 16]}>
        {reports.map((report) => (
          <Col key={report.id} xs={24} sm={8} md={8} lg={8}>
            <Card
              style={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
              }}
              bodyStyle={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '16px' }}
            >
              <div style={{ display: 'flex', gap: 16, flex: 1 }}>
                <div style={{ textAlign: 'center', minWidth: 60 }}>
                  {getFileIcon(report.type)}
                </div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: 4, fontSize: '14px' }}>
                      {report.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {report.type?.toUpperCase() || 'FILE'} • {formatFileSize(report.size)}
                    </Text>
                    {report.year && (
                      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
                        Year: {report.year}
                      </Text>
                    )}
                  </div>
                  <Space style={{ marginTop: 12 }}>
                    <Button
                      type="primary"
                      icon={<EyeOutlined />}
                      onClick={() => handleView(report)}
                      size="small"
                    >
                      View
                    </Button>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={() => handleDownload(report)}
                      size="small"
                    />
                  </Space>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      {reports.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
          No {reportType} reports found
        </div>
      )}
    </div>
  );
};
